import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '@asgardeo/auth-react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { CHAT_DB_SERVER_URL } from '../../config/backend-endpoints';

const CHAT_WORKSPACE_URL = import.meta.env.VITE_CHAT_DOMAIN_URL || 'https://chat.paralegal.lk';
const PRICING_PATH = '/chat-pricing';

const TRIAL_STATUS_OPTIONS = new Set([
  'trialing',
  'converted_to_paid',
  'expired',
  'access_removal_started',
]);

const formatDateTime = (date) => {
  const normalized = String(date || '').trim();
  if (!normalized) {
    return '-';
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString();
};

const formatDate = (date) => {
  const normalized = String(date || '').trim();
  if (!normalized) {
    return '-';
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDisplayLabel = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '-';
  }
  return normalized
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const normalizeTrialStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized || !TRIAL_STATUS_OPTIONS.has(normalized)) {
    return null;
  }
  return normalized;
};

const resolveTrialDaysRemaining = (value, trialEndsAt) => {
  if (value !== null && value !== undefined && value !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.ceil(parsed));
    }
  }

  const normalized = String(trialEndsAt || '').trim();
  if (!normalized) {
    return null;
  }
  const trialEndsAtMs = Date.parse(normalized);
  if (Number.isNaN(trialEndsAtMs)) {
    return null;
  }
  const diffMs = trialEndsAtMs - Date.now();
  return Math.max(0, Math.ceil(diffMs / 86400000));
};

const deriveAccountModel = (entitlementPayload) => {
  const entitlement = entitlementPayload || null;

  const chatAccess = typeof entitlement?.chat_access === 'boolean' ? entitlement.chat_access : false;
  const accessStatus = String(entitlement?.reason || (chatAccess ? 'active' : 'inactive'))
    .trim()
    .toLowerCase();

  const trialStatus = normalizeTrialStatus(entitlement?.trial_status);
  const trialStartedAt = entitlement?.trial_started_at || null;
  const trialEndsAt = entitlement?.trial_ends_at || null;
  const trialDaysRemaining = resolveTrialDaysRemaining(entitlement?.trial_days_remaining, trialEndsAt);
  const trialEligible = Boolean(entitlement?.trial_eligible);
  const hasTrialContext = Boolean(trialStatus || trialStartedAt || trialEndsAt);
  const effectiveFrom = entitlement?.effective_from || null;
  const effectiveTo = entitlement?.effective_to || null;
  const hasAccessPeriod = Boolean(effectiveFrom || effectiveTo);
  const hasSubscription = chatAccess || hasAccessPeriod;
  const isPaid = chatAccess && trialStatus !== 'trialing';
  const billingInterval = hasSubscription || hasTrialContext ? 'monthly' : null;
  const planName = hasSubscription || hasTrialContext || trialEligible ? 'Paralegal Chat Pro' : '-';
  const subscriptionStatus = chatAccess ? 'active' : accessStatus || 'inactive';
  const startDate = isPaid ? effectiveFrom || trialStartedAt : trialStartedAt || effectiveFrom;
  const endDate = isPaid ? effectiveTo || trialEndsAt : trialEndsAt || effectiveTo;

  let accountStateKey = 'no_subscription';
  if (accessStatus.includes('manual_review')) {
    accountStateKey = 'manual_review';
  } else if (accessStatus.includes('pending_activation') || accessStatus.includes('payment_verified')) {
    accountStateKey = 'pending_activation';
  } else if (accessStatus.includes('activation_failed') || accessStatus.includes('enqueue_failed')) {
    accountStateKey = 'activation_failed';
  } else if (trialStatus === 'trialing') {
    accountStateKey =
      trialDaysRemaining !== null && trialDaysRemaining <= 2 ? 'trial_ending_soon' : 'trial_active';
  } else if (trialStatus === 'expired' || trialStatus === 'access_removal_started') {
    accountStateKey = 'trial_expired_used';
  } else if (chatAccess) {
    accountStateKey = 'paid_active';
  } else if (accessStatus.includes('trial_already_used') || (trialStatus === 'converted_to_paid' && !chatAccess)) {
    accountStateKey = 'trial_expired_used';
  } else if (trialEligible || accessStatus === 'no_entitlement_record' || accessStatus === 'inactive') {
    accountStateKey = 'no_subscription';
  }

  const stateConfig = {
    no_subscription: {
      label: 'No Active Subscription',
      title: 'No active subscription',
      message: 'Start your free trial to access chat.paralegal.lk.',
      action: { type: 'link', label: 'Start Free Trial', to: PRICING_PATH },
    },
    trial_active: {
      label: 'Trial Active',
      title: 'Free trial active',
      message:
        trialDaysRemaining !== null
          ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining. Subscribe before trial end to continue access.`
          : 'Your free trial is active. Subscribe before it ends to continue access.',
      action: { type: 'link', label: 'Subscribe Now', to: PRICING_PATH },
    },
    trial_ending_soon: {
      label: 'Trial Ending Soon',
      title: 'Trial ending soon',
      message:
        trialDaysRemaining !== null
          ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining. Subscribe now to avoid interruption.`
          : 'Subscribe now to avoid interruption when trial ends.',
      action: { type: 'link', label: 'Subscribe Now', to: PRICING_PATH },
    },
    trial_expired_used: {
      label: 'Trial Ended',
      title: 'Trial ended',
      message: 'Free trial is no longer active for this account. Subscribe to restore access.',
      action: { type: 'link', label: 'Start Subscription', to: PRICING_PATH },
    },
    paid_active: {
      label: 'Subscription Active',
      title: 'Subscription active',
      message: 'Your subscription is active. You can continue using chat.',
      action: { type: 'external', label: 'Open Chat', href: CHAT_WORKSPACE_URL },
    },
    pending_activation: {
      label: 'Pending Activation',
      title: 'Activating access',
      message: 'Payment or trial is confirmed and access activation is in progress.',
      action: { type: 'refresh', label: 'Refresh Status' },
    },
    manual_review: {
      label: 'Manual Review',
      title: 'Manual review',
      message: 'Your payment is being reviewed. Access will activate after verification.',
      action: { type: 'refresh', label: 'Refresh Status' },
    },
    activation_failed: {
      label: 'Activation Failed',
      title: 'Activation failed',
      message: 'Please retry from Pricing or contact support for assistance.',
      action: { type: 'link', label: 'View Pricing', to: PRICING_PATH },
    },
  };

  const selectedState = stateConfig[accountStateKey] || stateConfig.no_subscription;

  const periodLabels = {
    start: 'Effective from',
    end: 'Effective to',
  };

  if (accountStateKey === 'trial_active' || accountStateKey === 'trial_ending_soon') {
    periodLabels.start = 'Trial started';
    periodLabels.end = 'Trial ends';
  } else if (accountStateKey === 'paid_active') {
    periodLabels.start = 'Current period start';
    periodLabels.end = 'Current period end';
  } else if (hasSubscription) {
    periodLabels.start = 'Subscription start';
    periodLabels.end = 'Subscription end';
  }

  const periodStartValue =
    accountStateKey === 'trial_active' || accountStateKey === 'trial_ending_soon'
      ? formatDateTime(trialStartedAt || startDate)
      : formatDateTime(startDate);

  const periodEndValue =
    accountStateKey === 'trial_active' || accountStateKey === 'trial_ending_soon'
      ? formatDateTime(trialEndsAt || endDate)
      : formatDateTime(endDate);

  return {
    planName,
    subscriptionStatus,
    accessStatus,
    chatAccess,
    billingInterval,
    startDate,
    endDate,
    trialStatus,
    trialEndsAt,
    trialDaysRemaining,
    isPaid,
    hasSubscription,
    accountStateLabel: selectedState.label,
    accountStateTitle: selectedState.title,
    accountStateMessage: selectedState.message,
    primaryAccountAction: selectedState.action,
    periodLabels,
    periodStartValue,
    periodEndValue,
  };
};

const Account = () => {
  const { state, getAccessToken, getBasicUserInfo, getDecodedIDToken } = useAuthContext();

  const [userInfo, setUserInfo] = useState({
    username: '',
    givenname: '',
    email: '',
  });
  const [entitlementPayload, setEntitlementPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const runWithTimeout = useCallback((promise, timeoutMs = 8000) => {
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        reject(new Error('Request timeout'));
      }, timeoutMs);

      promise
        .then((value) => {
          window.clearTimeout(timer);
          resolve(value);
        })
        .catch((requestError) => {
          window.clearTimeout(timer);
          reject(requestError);
        });
    });
  }, []);

  const loadAccountData = useCallback(async () => {
    if (!state?.isAuthenticated) {
      setLoading(false);
      setEntitlementPayload(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [info, decodedToken] = await Promise.all([
        getBasicUserInfo().catch(() => ({})),
        getDecodedIDToken().catch(() => ({})),
      ]);
      const email = info?.email || decodedToken?.email || '';
      const username = info?.displayName || info?.givenName || '';
      const givenname = info?.givenName || info?.displayName || '';

      setUserInfo({ username, givenname, email });

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Authentication token is missing.');
      }

      const entitlementResponse = await runWithTimeout(
        fetch(`${CHAT_DB_SERVER_URL}/chat-entitlements/me`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }),
        8000
      );

      const entitlementPayload = await entitlementResponse.json().catch(() => ({}));
      if (!entitlementResponse.ok) {
        throw new Error(
          entitlementPayload?.detail || entitlementPayload?.error || `HTTP ${entitlementResponse.status}`
        );
      }
      setEntitlementPayload(entitlementPayload);
    } catch (fetchError) {
      setEntitlementPayload(null);
      setError(fetchError?.message || 'Unable to load access details. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [
    getAccessToken,
    getBasicUserInfo,
    getDecodedIDToken,
    runWithTimeout,
    state?.isAuthenticated,
  ]);

  useEffect(() => {
    loadAccountData();
  }, [loadAccountData]);

  const accountModel = useMemo(
    () => deriveAccountModel(entitlementPayload),
    [entitlementPayload]
  );

  const renderPrimaryAction = (action) => {
    if (!action) {
      return null;
    }

    if (action.type === 'external') {
      return (
        <a
          href={action.href}
          className="inline-flex items-center justify-center rounded-pl bg-pl-accent px-5 py-2.5 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover"
        >
          {action.label}
        </a>
      );
    }

    if (action.type === 'link') {
      return (
        <Link
          to={action.to}
          className="inline-flex items-center justify-center rounded-pl bg-pl-accent px-5 py-2.5 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover"
        >
          {action.label}
        </Link>
      );
    }

    return (
      <button
        type="button"
        onClick={loadAccountData}
        className="inline-flex items-center justify-center rounded-pl bg-pl-accent px-5 py-2.5 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover"
      >
        {action.label}
      </button>
    );
  };

  return (
    <MainLayout>
      <Helmet>
        <title>My Account | paralegal.lk</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 md:p-8 lg:p-12 mt-30 lg:mt-10 mb-[-25px]">
        <div className="mx-auto max-w-7xl space-y-8">
          <section className="rounded-[20px] border border-pl-border bg-white p-6 shadow-pl-sm md:p-8">
            <h1 className="text-3xl font-display font-semibold text-pl-text-primary md:text-4xl">
              Welcome,{' '}
              {loading ? (
                <span className="inline-block h-8 w-32 animate-pulse rounded bg-gray-200" />
              ) : (
                <span className="text-pl-accent">{userInfo.givenname || 'there'}!</span>
              )}
            </h1>
            <p className="mt-2 text-16 text-pl-text-secondary">
              Manage your account settings and subscription access from one place.
            </p>
          </section>

          {error && (
            <section className="rounded-[16px] border border-pl-warning/30 bg-[rgba(188,108,37,0.08)] px-5 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-14 text-pl-text-primary">{error}</p>
                <button
                  type="button"
                  onClick={loadAccountData}
                  className="inline-flex items-center justify-center rounded-pl border border-pl-warning px-4 py-2 text-13 font-semibold text-pl-warning transition-colors hover:bg-pl-warning hover:text-white"
                >
                  Retry
                </button>
              </div>
            </section>
          )}

          <section className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            <article className="rounded-[20px] border border-pl-border bg-white p-6 shadow-pl-sm md:p-8">
              <h2 className="text-22 font-display font-semibold text-pl-text-primary">Personal Details</h2>
              <div className="mt-6 space-y-4 text-14">
                <div className="flex items-center justify-between gap-4 border-b border-pl-border pb-3">
                  <span className="text-pl-text-tertiary">Name</span>
                  <span className="font-medium text-pl-text-primary">{loading ? 'Loading...' : userInfo.username || '-'}</span>
                </div>
                <div className="flex items-center justify-between gap-4 border-b border-pl-border pb-3">
                  <span className="text-pl-text-tertiary">Email</span>
                  <span className="font-medium text-pl-text-primary">{loading ? 'Loading...' : userInfo.email || '-'}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  window.location.href = `https://accounts.asgardeo.io/t/paralegallk/accountrecoveryendpoint/recoveraccountrouter.do?client_id=${import.meta.env.VITE_ASGARDEO_CLIENT_ID}&redirect_uri=${import.meta.env.VITE_SIGN_IN_URL}`;
                }}
                className="mt-8 inline-flex w-full items-center justify-center rounded-pl bg-pl-accent px-5 py-2.5 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover"
              >
                Reset Password
              </button>
            </article>

            <article className="rounded-[20px] border border-pl-border bg-white p-6 shadow-pl-sm md:p-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-12 font-semibold uppercase tracking-[0.16em] text-pl-accent">Subscription Center</p>
                  <h2 className="mt-2 text-22 font-display font-semibold text-pl-text-primary">Subscription & Access</h2>
                </div>
                <button
                  type="button"
                  onClick={loadAccountData}
                  className="inline-flex items-center justify-center rounded-pl border border-pl-border px-4 py-2 text-13 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle"
                >
                  Refresh Status
                </button>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <div className="rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">Plan</p>
                  <p className="mt-1 text-14 font-semibold text-pl-text-primary">{loading ? 'Loading...' : accountModel.planName}</p>
                </div>
                <div className="rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">Status</p>
                  <p className="mt-1 text-14 font-semibold text-pl-text-primary">{loading ? 'Loading...' : accountModel.accountStateLabel}</p>
                </div>
                <div className="rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">Billing Cycle</p>
                  <p className="mt-1 text-14 font-semibold text-pl-text-primary">{loading ? 'Loading...' : formatDisplayLabel(accountModel.billingInterval)}</p>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-pl border border-pl-border bg-white px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">{accountModel.periodLabels.start}</p>
                  <p className="mt-1 text-14 font-medium text-pl-text-primary">{loading ? 'Loading...' : accountModel.periodStartValue}</p>
                </div>
                <div className="rounded-pl border border-pl-border bg-white px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">{accountModel.periodLabels.end}</p>
                  <p className="mt-1 text-14 font-medium text-pl-text-primary">{loading ? 'Loading...' : accountModel.periodEndValue}</p>
                </div>
              </div>

              <div className="mt-6 rounded-[16px] border border-pl-border bg-pl-bg-elevated px-5 py-4">
                <h3 className="text-16 font-semibold text-pl-text-primary">{loading ? 'Checking account state...' : accountModel.accountStateTitle}</h3>
                <p className="mt-2 text-14 leading-7 text-pl-text-secondary">
                  {loading ? 'Loading the latest subscription and access details.' : accountModel.accountStateMessage}
                </p>

                {!loading && accountModel.trialEndsAt && (
                  <p className="mt-2 text-13 text-pl-text-tertiary">
                    Trial end date: {formatDate(accountModel.trialEndsAt)}
                  </p>
                )}

                {!loading && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    {renderPrimaryAction(accountModel.primaryAccountAction)}
                    <Link
                      to={PRICING_PATH}
                      className="inline-flex items-center justify-center rounded-pl border border-pl-border px-5 py-2.5 text-14 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle"
                    >
                      View Pricing
                    </Link>
                    {(accountModel.accountStateLabel === 'Activation Failed' || accountModel.accountStateLabel === 'Manual Review') && (
                      <a
                        href="mailto:admin@paralegal.lk?subject=Chat%20Subscription%20Support"
                        className="inline-flex items-center justify-center rounded-pl border border-pl-border px-5 py-2.5 text-14 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle"
                      >
                        Contact Support
                      </a>
                    )}
                  </div>
                )}
              </div>
            </article>
          </section>
        </div>
      </div>
    </MainLayout>
  );
};

export default Account;
